import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';

import { TranslateText } from '../../../../application/language/TranslateText';
import { useI18n } from '../../../../application/language/useI18n';
import { EmptyPage } from '../../../design/emptyPage/EmptyPage';
import { PageLoader } from '../../../design/page/PageLoader';
import { IMainLayoutDomainStore } from '../../../layout/main/store/domain/IMainLayoutDomainStore';
import { useProgressiveRender } from '../../../shared/hooks/useProgressiveRender';
import { UnitListAdminCard } from './components/UnitListAdminCard';
import adminEmptyPage from './parts/adminEmptyPage.png';
import { UnitListCard } from './parts/UnitListCard';
import { IUnitListAdminPageDomain } from './store/IUnitListAdminPageDomain';

enum FilterValues {
  All = 'all',
  Published = 'published',
  NotPublished = 'notPublished',
}

export interface IUnitListAdminPage {
  layoutDomain: IMainLayoutDomainStore;
}

export const UnitListAdminPage = observer(({ domain }: { domain: IUnitListAdminPageDomain }) => {
  const { translate } = useI18n();

  useEffect(() => {
    domain.loadData();
  }, [domain]);

  const filter = domain.ui.filterByIsPublished.value;
  const groupsList = domain.ui.filtredGroupedUnits.list;

  const filteredGroups = useMemo(() => {
    return groupsList
      .map((group) => {
        const { unitsList } = group;
        const filteredUnits =
          filter === FilterValues.All
            ? unitsList
            : filter === FilterValues.Published
            ? unitsList.filter((unit) => unit.isPublished)
            : unitsList.filter((unit) => !unit.isPublished);
        return { ...group, unitsList: filteredUnits };
      })
      .filter((group) => group.unitsList.length > 0);
  }, [groupsList, filter]);

  const flatUnits = useMemo(() => filteredGroups.flatMap((group) => group.unitsList), [filteredGroups]);

  const { visibleItems, hasMore, sentinelRef, scrollToId } = useProgressiveRender(flatUnits);

  const hasUnits = filteredGroups.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {domain.ui.isLoading.value ? (
        <PageLoader minHeight="100%" />
      ) : (
        <>
          {domain.ui.unitList.list.length === 0 ? (
            <EmptyPage
              image={adminEmptyPage}
              title={translate('learningAdminPage.createUnitLabel')}
              buttonTitle={translate('learningAdminPage.createUnit')}
              buttonAction={domain.redirectToCreateUnit}
            />
          ) : (
            <Box sx={{ display: 'flex', gap: '16px', paddingTop: '16px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Box
                sx={{
                  flex: '0 0 28%',
                  gap: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  paddingBottom: '16px',
                }}
              >
                {filteredGroups.map((group) => (
                  <UnitListCard key={group.groupName} title={group.groupName} units={group.unitsList} domain={domain} scrollToId={scrollToId} />
                ))}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  overflowY: 'auto',
                }}
                id="units-container"
              >
                {!hasUnits && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                      height: '100%',
                    }}
                  >
                    <Typography variant="h5">
                      <TranslateText i18nKey="learningAdminPage.noUnits" />
                    </Typography>
                  </Box>
                )}

                {visibleItems.map((unit) => (
                  <UnitListAdminCard
                    key={unit.id}
                    handleEdit={domain.onEditUnit}
                    handleDelete={domain.onDeleteUnit}
                    handlePublish={domain.publishUnit}
                    handleUnpublish={domain.unpublishUnit}
                    unit={unit}
                  />
                ))}
                {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
});
